import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button } from "@/components/ui/button";
import { FeedbackMessage } from "@/components/ui/feedback";
import { Input, Label, Textarea, FieldError } from "@/components/ui/input";
import { Seo } from "@/components/Seo";
import { invokeFunction, ApiError } from "@/lib/api";
import { applicationSchema, type ApplicationInput } from "@/features/forms/schemas";
import { company } from "@/content/site";
import { getJob, listPublishedJobs } from "@/features/jobs/repo";
import { createLocalApplication } from "@/features/applications/repo";
import type { Job } from "@/content/jobs";

const RESUME_MAX_BYTES = 5 * 1024 * 1024; // 5 MB
const RESUME_TYPES = [
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
];

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    // result is a data URL ("data:<mime>;base64,<data>") - keep only the data.
    reader.onload = () => resolve(String(reader.result).split(",")[1] ?? "");
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

export default function Apply() {
  const [params] = useSearchParams();
  const typeParam = params.get("type");
  const initialType: ApplicationInput["type"] =
    typeParam === "carrier" ? "carrier" : typeParam === "office" ? "office" : "driver";
  const jobSlug = params.get("job") ?? "";
  const [job, setJob] = useState<Job | undefined>();
  const [jobs, setJobs] = useState<Job[]>([]);
  const [selection, setSelection] = useState(`general:${initialType}`);
  const [resumeFile, setResumeFile] = useState<File | null>(null);
  const [resumeError, setResumeError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<ApplicationInput>({
    resolver: zodResolver(applicationSchema),
    defaultValues: { type: initialType, position: jobSlug },
  });

  // Sync the form type/position + "Applying for" banner from a dropdown choice.
  // A "general:*" key is an evergreen application; a "job:<slug>" key is one of
  // the staff-managed open positions.
  const applySelection = useCallback(
    (value: string, options: Job[]) => {
      setSelection(value);
      if (value.startsWith("job:")) {
        const slug = value.slice(4);
        const found = options.find((j) => j.slug === slug);
        setJob(found);
        setValue("type", found ? found.applyType : "driver");
        setValue("position", slug);
      } else {
        setJob(undefined);
        setValue(
          "type",
          value === "general:carrier" ? "carrier" : value === "general:office" ? "office" : "driver",
        );
        setValue("position", "");
      }
    },
    [setValue],
  );

  // Populate the dropdown from the live (staff-managed) open positions. The two
  // evergreen general options are always present; specific postings come from
  // the jobs repo (Supabase, or the localStorage fallback in preview mode), so
  // anything staff publish on the careers page shows up here automatically.
  useEffect(() => {
    let active = true;
    (async () => {
      const fallback = `general:${initialType}`;
      try {
        const published = await listPublishedJobs();
        let options = published;
        let initialSel = fallback;
        if (jobSlug) {
          let found = published.find((j) => j.slug === jobSlug);
          if (!found) {
            // Honor a direct link to a posting that isn't in the published list.
            found = await getJob(jobSlug);
            if (found) options = [...published, found];
          }
          if (found) initialSel = `job:${found.slug}`;
        }
        if (!active) return;
        setJobs(options);
        applySelection(initialSel, options);
      } catch {
        if (active) applySelection(fallback, []);
      }
    })();
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const type = watch("type");
  const authority = watch("authority");
  const source = watch("source");
  const isGeneral = selection.startsWith("general:");

  function onResumeChange(file: File | null) {
    setResumeError(null);
    if (!file) {
      setResumeFile(null);
      return;
    }
    if (!RESUME_TYPES.includes(file.type)) {
      setResumeError("Please upload a PDF or Word document.");
      return;
    }
    if (file.size > RESUME_MAX_BYTES) {
      setResumeError("That file is over 5 MB - please upload a smaller one.");
      return;
    }
    setResumeFile(file);
  }

  async function onSubmit(values: ApplicationInput) {
    setServerError(null);
    try {
      let body: Record<string, unknown> = { ...values };
      // Office applications may attach a resume; it's stored server-side in the
      // PRIVATE documents bucket (never a public URL).
      if (values.type === "office" && resumeFile) {
        body = {
          ...body,
          resume: {
            name: resumeFile.name,
            type: resumeFile.type,
            base64: await fileToBase64(resumeFile),
          },
        };
      }
      await invokeFunction("applications", body);
      setSent(true);
    } catch (err) {
      if (err instanceof ApiError && err.status === 503) {
        await createLocalApplication(values, resumeFile?.name);
        setSent(true);
        return;
      }
      setServerError("Something went wrong. Please try again or call us.");
    }
  }

  if (sent) {
    return (
      <section className="container py-20">
        <div className="mx-auto max-w-lg">
          <FeedbackMessage tone="success">
            Application received. Thanks for applying. Our team will review and reach out.
          </FeedbackMessage>
        </div>
      </section>
    );
  }

  return (
    <>
      <Seo
        title={`Apply | ${company.name}`}
        description="Apply as a company driver, owner-operator, or for an office role at Bull and Bear Co."
      />
      <section className="bg-brand-50">
        <div className="container py-16">
          <h1 className="text-3xl font-bold md:text-4xl">Drive with us</h1>
          <p className="mt-3 max-w-prose text-brand-700">
            {job
              ? `You're applying for the ${job.title} position. Fill out the form below and our team will reach out.`
              : "Apply as a company driver, owner-operator, or for an office role - we'll reach out fast."}
          </p>
        </div>
      </section>

      <section className="container py-12">
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="max-w-xl space-y-4"
          noValidate
        >
          <input type="hidden" {...register("type")} />
          <input type="hidden" {...register("position")} />
          {job && (
            <div className="rounded-md border border-accent-300 bg-accent-500/10 px-4 py-3 text-sm text-brand-800">
              Applying for: <span className="font-semibold">{job.title}</span>
            </div>
          )}
          <div>
            <Label htmlFor="position-select">I'm applying for</Label>
            <select
              id="position-select"
              value={selection}
              onChange={(e) => applySelection(e.target.value, jobs)}
              className="flex h-10 w-full rounded-md border border-brand-200 bg-white px-3 text-sm"
            >
              <optgroup label="General - we're always hiring">
                <option value="general:driver">Company driver</option>
                <option value="general:carrier">Owner-operator</option>
                <option value="general:office">Office / something else</option>
              </optgroup>
              {jobs.length > 0 && (
                <optgroup label="Open positions">
                  {jobs.map((j) => (
                    <option key={j.slug} value={`job:${j.slug}`}>
                      {j.title}
                    </option>
                  ))}
                </optgroup>
              )}
            </select>
            <p className="mt-1 text-xs text-brand-500">
              Don't see your exact role? Pick the closest general option - we
              hire company drivers and owner-operators year-round.
            </p>
          </div>

          {isGeneral && type === "office" && (
            <div>
              <Label htmlFor="interest">What kind of job are you interested in?</Label>
              <Input
                id="interest"
                placeholder="e.g. Safety assistant, dispatch, accounting..."
                {...register("interest")}
                aria-invalid={!!errors.interest}
              />
              <FieldError>{errors.interest?.message}</FieldError>
            </div>
          )}

          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <Label htmlFor="name">Name</Label>
              <Input id="name" {...register("name")} aria-invalid={!!errors.name} />
              <FieldError>{errors.name?.message}</FieldError>
            </div>
            <div>
              <Label htmlFor="phone">Phone</Label>
              <Input id="phone" {...register("phone")} aria-invalid={!!errors.phone} />
              <FieldError>{errors.phone?.message}</FieldError>
            </div>
          </div>

          <div>
            <Label htmlFor="email">Email</Label>
            <Input id="email" type="email" {...register("email")} aria-invalid={!!errors.email} />
            <FieldError>{errors.email?.message}</FieldError>
          </div>

          {type === "driver" && (
            <>
              <div className="grid gap-4 sm:grid-cols-3">
                <div>
                  <Label htmlFor="cdlState">CDL state</Label>
                  <Input
                    id="cdlState"
                    maxLength={2}
                    placeholder="IL"
                    {...register("cdlState")}
                    aria-invalid={!!errors.cdlState}
                  />
                  <FieldError>{errors.cdlState?.message}</FieldError>
                </div>
                <div>
                  <Label htmlFor="experienceYears">Years of CDL experience</Label>
                  <Input
                    id="experienceYears"
                    type="number"
                    min={0}
                    {...register("experienceYears")}
                    aria-invalid={!!errors.experienceYears}
                  />
                  <FieldError>{errors.experienceYears?.message}</FieldError>
                </div>
                <div>
                  <Label htmlFor="cdlNumber">
                    CDL number <span className="font-normal text-brand-400">(optional)</span>
                  </Label>
                  <Input id="cdlNumber" {...register("cdlNumber")} />
                </div>
              </div>
              <fieldset>
                <legend className="text-sm font-medium text-brand-800">
                  Endorsements <span className="font-normal text-brand-400">(check all that apply)</span>
                </legend>
                <div className="mt-2 flex flex-wrap gap-x-6 gap-y-2 text-sm text-brand-700">
                  <label className="flex items-center gap-2">
                    <input type="checkbox" className="h-4 w-4" {...register("endorsementTanker")} />
                    Tanker (N)
                  </label>
                  <label className="flex items-center gap-2">
                    <input type="checkbox" className="h-4 w-4" {...register("endorsementHazmat")} />
                    HazMat (H)
                  </label>
                  <label className="flex items-center gap-2">
                    <input type="checkbox" className="h-4 w-4" {...register("endorsementTwic")} />
                    TWIC card
                  </label>
                </div>
              </fieldset>
              <fieldset>
                <legend className="text-sm font-medium text-brand-800">
                  Tanker offloading experience <span className="font-normal text-brand-400">(check all that apply)</span>
                </legend>
                <div className="mt-2 flex flex-wrap gap-x-6 gap-y-2 text-sm text-brand-700">
                  <label className="flex items-center gap-2">
                    <input type="checkbox" className="h-4 w-4" {...register("offloadAirCompressor")} />
                    Offloading with an air compressor
                  </label>
                  <label className="flex items-center gap-2">
                    <input type="checkbox" className="h-4 w-4" {...register("offloadPump")} />
                    Offloading with a pump
                  </label>
                </div>
              </fieldset>
            </>
          )}
          {type === "carrier" && (
            <>
              <div>
                <Label htmlFor="authority">Operating authority</Label>
                <select
                  id="authority"
                  className="flex h-10 w-full rounded-md border border-brand-200 bg-white px-3 text-sm"
                  {...register("authority")}
                  aria-invalid={!!errors.authority}
                >
                  <option value="">Select...</option>
                  <option value="own">I have my own MC/DOT authority</option>
                  <option value="lease">I want to run under Bull &amp; Bear's authority</option>
                </select>
                <FieldError>{errors.authority?.message}</FieldError>
              </div>
              {authority === "own" && (
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <Label htmlFor="dotNumber">DOT number</Label>
                    <Input
                      id="dotNumber"
                      {...register("dotNumber")}
                      aria-invalid={!!errors.dotNumber}
                    />
                    <FieldError>{errors.dotNumber?.message}</FieldError>
                  </div>
                  <div>
                    <Label htmlFor="mcNumber">
                      MC number <span className="font-normal text-brand-400">(optional)</span>
                    </Label>
                    <Input id="mcNumber" {...register("mcNumber")} />
                  </div>
                </div>
              )}
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <Label htmlFor="truck">Truck (year, make &amp; model)</Label>
                  <Input
                    id="truck"
                    placeholder="e.g. 2021 Freightliner Cascadia"
                    {...register("truck")}
                    aria-invalid={!!errors.truck}
                  />
                  <FieldError>{errors.truck?.message}</FieldError>
                </div>
                <div>
                  <Label htmlFor="experienceYears">Years of CDL experience</Label>
                  <Input
                    id="experienceYears"
                    type="number"
                    min={0}
                    {...register("experienceYears")}
                    aria-invalid={!!errors.experienceYears}
                  />
                  <FieldError>{errors.experienceYears?.message}</FieldError>
                </div>
              </div>
            </>
          )}
          {type === "office" && (
            <div>
              <Label htmlFor="resume">
                Resume <span className="font-normal text-brand-400">(PDF or Word, max 5 MB)</span>
              </Label>
              <input
                id="resume"
                type="file"
                accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                onChange={(e) => onResumeChange(e.target.files?.[0] ?? null)}
                className="block w-full cursor-pointer rounded-md border border-brand-200 bg-white text-sm text-brand-700 file:mr-2 file:cursor-pointer file:rounded-l-md file:border-0 file:bg-brand-900 file:px-3 file:py-2.5 file:text-sm file:font-medium file:text-white hover:file:bg-brand-800 sm:file:mr-3 sm:file:px-4"
              />
              {resumeFile && !resumeError && (
                <p className="mt-1 text-xs text-green-700">
                  Attached: <span className="break-all">{resumeFile.name}</span>
                </p>
              )}
              {resumeError && <p className="mt-1 text-xs text-red-600">{resumeError}</p>}
            </div>
          )}

          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <Label htmlFor="source">How did you hear about us?</Label>
              <select
                id="source"
                className="flex h-10 w-full rounded-md border border-brand-200 bg-white px-3 text-sm"
                {...register("source")}
                aria-invalid={!!errors.source}
              >
                <option value="">Select...</option>
                <option value="google">Google / web search</option>
                <option value="indeed">Indeed or job board</option>
                <option value="facebook">Facebook / social media</option>
                <option value="referral">Referral from someone</option>
                <option value="truck">Saw a Bull &amp; Bear truck</option>
                <option value="other">Other</option>
              </select>
              <FieldError>{errors.source?.message}</FieldError>
            </div>
            {source === "referral" && (
              <div>
                <Label htmlFor="referralName">Referral name</Label>
                <Input
                  id="referralName"
                  placeholder="Who referred you?"
                  {...register("referralName")}
                  aria-invalid={!!errors.referralName}
                />
                <FieldError>{errors.referralName?.message}</FieldError>
              </div>
            )}
          </div>

          <div>
            <Label htmlFor="notes">Anything else?</Label>
            <Textarea id="notes" {...register("notes")} />
          </div>

          <div>
            <label className="flex items-start gap-3 rounded-md border border-brand-100 bg-brand-50 p-3 text-sm text-brand-700">
              <input
                type="checkbox"
                className="mt-0.5 h-4 w-4 shrink-0"
                {...register("consentToContact")}
                aria-invalid={!!errors.consentToContact}
              />
              <span>
                I agree that Bull &amp; Bear may contact me by phone, text, or
                email about this application. Message/data rates may apply.
              </span>
            </label>
            <FieldError>{errors.consentToContact?.message}</FieldError>
          </div>

          {/* Honeypot */}
          <input
            type="text"
            tabIndex={-1}
            autoComplete="off"
            className="hidden"
            aria-hidden="true"
            {...register("company_website")}
          />

          <FeedbackMessage tone="error">{serverError}</FeedbackMessage>

          <p className="text-xs text-brand-500">
            {type === "office"
              ? "Your resume is stored securely and only visible to our hiring team."
              : "Document upload (CDL, insurance, W9) happens securely after your application is reviewed - never attach sensitive documents here."}
          </p>

          <Button type="submit" variant="accent" size="lg" className="w-full sm:w-auto" disabled={isSubmitting}>
            {isSubmitting ? "Submitting..." : "Submit application"}
          </Button>
        </form>
      </section>
    </>
  );
}
